import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '../ui-icons/ui-close-icon';
import type {
  AccountStatusServiceStore,
  GlobalStatusBanner,
} from '../../../app/package/base/service/account-status/account-status-service';
import type { SowBannerEventAction, SowWidget } from '../../../app/package/base/service/tracking/tracking-service';
import { SubHelper } from '../../base/package/patterns/package/store';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { MessageTypes } from '../../base/package/types';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import styles from './ui-global-status-banner.css?inline';
import '../ui-message/ui-message';

const CName = 'ui-global-status-banner';

@customElement(CName)
export class UIGlobalStatusBanner extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @state() private _banner?: GlobalStatusBanner;

  private readonly _subHelper = new SubHelper();

  get canClose() {
    if (this._banner?.type) {
      return ([MessageTypes.Default, MessageTypes.Info] as MessageTypes[]).includes(this._banner?.type);
    }

    return false;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._subHelper.addSub(window.$app.accountStatusStore, (_) => this._updateBanner(_), true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  render() {
    //this._showDummyBanner();

    if (!this._banner?.visible || !this._banner.message) {
      return nothing;
    }

    return html`
      <div class="wrapper">
        <ui-message class=${ifDefined(this._banner.type)} icon ?close=${this.canClose} shouldRemoveTopPadding>
          ${this._banner.title ? html`<p class="title">${this._banner.title}</p>` : nothing}
          <p class="message">${this._banner.message}</p>
          <div slot="action"
            tabindex="0"
            role="button"
            @click=${this._onAccept}
            @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._onAccept.bind(this))}
          >${this._banner.action}</div>
          <div slot="close"
            tabindex="0"
            @click=${this._onClose}
            @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._onClose.bind(this))}
          >
            <button class="close-button" aria-label=${window.$app.$t.get(I18n.a11y.button.close)}>
              <ui-close-icon></ui-close-icon>
            </button>
          </div>
        </ui-message>
      </div>
    `;
  }

  /*
  private _showDummyBanner() {
    this._banner = {
      action: 'No action',
      message: `${'This is a dummy banner. It is used to test the UI. It is not a real banner. It is a test banner. '.repeat(3)}`,
      //message: `${'This is a dummy banner. It is used to test the UI. It is not a real banner. It is a test banner. '.repeat(5)}UnexpectedSuperLongGermanWordRequiresGroundbreakingLayoutCapabilitiesOnMobileDevicesAndSmallScreens.`,
      type: MessageTypes.Info,
      title: 'Dummy banner Title',
      visible: true,
    };
  }
*/

  private _onAccept() {
    if (this._banner?.callbacks?.onAcceptCallback) {
      this._triggerSowTracking('Start');
      this._triggerLegacyTracking('start');
      this._banner.callbacks.onAcceptCallback();
    }
  }

  private _onClose() {
    if (this.canClose) {
      window.$app.accountStatusStore.next({
        banner: {
          ...this._banner,
          visible: false,
        },
      });
      if (this._banner?.callbacks?.onCloseCallback) {
        this._banner.callbacks.onCloseCallback();
      }
      this._triggerSowTracking('Close');
      this._triggerLegacyTracking('close');
    }
  }

  private _updateBanner(store: AccountStatusServiceStore) {
    const wasVisible = this._banner?.visible && this._banner?.trackingKey;
    this._banner = store.banner;
    if (!wasVisible && this._banner?.visible && this._banner?.trackingKey) {
      this._triggerSowTracking('Shown');
      this._triggerLegacyTracking('shown');
    }
  }

  private _triggerSowTracking(eventAction: SowBannerEventAction) {
    if (!this._banner?.trackingWidget || !this._banner?.trackingUserState) {
      return;
    }

    window.$app.track.sourceOfWealth({
      eventAction,
      sowType: 'Games',
      userState: this._banner.trackingUserState,
      widget: this._banner.trackingWidget as SowWidget,
    });
  }

  private _triggerLegacyTracking(eventAction: 'shown' | 'start' | 'close') {
    if (!this._banner?.trackingKey) {
      return;
    }

    window.$app.track.lobbyBanners({
      banner: this._banner.trackingKey,
      eventAction,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIGlobalStatusBanner;
  }
}
